import React, { ReactNode, useEffect, useState } from 'react';
import styles from './style.less';
import {
  CloseCircleTwoTone,
  InfoCircleTwoTone,
  LoadingOutlined,
  ScanOutlined,
  SecurityScanTwoTone,
} from '@ant-design/icons';
import { APIError } from '../../api/types';
import Loading from '../Loading';
import { playSound } from '@/utils/sound';
import { addWsListener, DOMAIN_PAYMENT, removeWsListener, WsActions } from '../../api/ws';
import { addGlobalScanListener, removeGlobalScanListener, removeKeyDownListener, ScanType } from './GlobalScanner';
import PosButton from '../PosButton';
import { PaymentChannel, PosCodePayProceedingInfo } from '@/models/payment';

const modeValidator = {
  payAuthCode: v => v => /^\d{18}$/.test(v),
  skuSn: v => v => /^\d{10,}$/.test(v),
};

function drawLine(canvas, begin, end) {
  canvas.beginPath();
  canvas.moveTo(begin.x, begin.y);
  canvas.lineTo(end.x, end.y);
  canvas.lineWidth = 4;
  canvas.strokeStyle = '#FF3B58';
  canvas.stroke();
}

type QRScannerProps = {
  loading?: boolean;
  error?: APIError;
  onChange?: (code: string) => void;
  indicator?: ReactNode;
  renderInit?: () => ReactNode;
  renderLoading?: () => ReactNode;
  renderError?: (error: APIError) => ReactNode;
  scanType: ScanType;
  // onCode?: (code: string) => void;
};

const QRScanner: React.FC<QRScannerProps> = ({
  onChange,
  loading = false,
  error,
  scanType,
  indicator = <div draggable={false} className={styles.introImg} />,
  renderInit = () => (
    <>
      <InfoCircleTwoTone className={styles.icon} />
      <div>请扫码</div>
    </>
  ),
  renderError = () => (
    <>
      <CloseCircleTwoTone twoToneColor={'#eb2f96'} className={styles.icon} />
      <div>{error.msg}</div>
    </>
  ),
  renderLoading = () => (
    <>
      <Loading className={styles.icon} />
      <div>查询中，请等待</div>
    </>
  ),
}: // onCode = () => {},
QRScannerProps) => {
  // const [code, setCode] = useState<string>('');
  // const containerRef = useRef<HTMLDivElement>(null);
  // const keyPressHandler = e => {
  //   const tagName = e.target.tagName.toLowerCase();
  //   if (tagName === 'input' || tagName === 'textarea') {
  //     return;
  //   }
  //
  //   // e.stopPropagation();
  //   e.preventDefault();
  //   if (e.key === 'Enter') {
  //     e.stopPropagation();
  //     playSound('scan_di');
  //     onChange(code);
  //     setCode('');
  //     onCode('');
  //   } else if (e.key === 'Backspace') {
  //     const c = code.substring(0, code.length - 1);
  //     setCode(c);
  //     onCode(c);
  //   } else {
  //     // 扫码枪会输入 `shift` 等辅助按键
  //     if (e.key.length === 1) {
  //       const c = code + e.key;
  //       setCode(c);
  //       onCode(c);
  //     }
  //   }
  // };

  useEffect(() => {
    const key = addGlobalScanListener(scanType, ({ code }) => {
      playSound('scan_di');
      onChange(code);
    });

    return () => {
      removeGlobalScanListener(key);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.intro}>
        {indicator}
        <div className={styles.main}>
          {loading && renderLoading()}
          {error && !loading && renderError(error)}
          {!error && !loading && renderInit()}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;

type GoodsSnScannerProps = Omit<QRScannerProps, 'scanType'> & {};
export const GoodsSnScanner: React.FC<GoodsSnScannerProps> = ({
  onChange: onChangeProp,
  ...qrScannerProps
}: PayQRScannerProps) => {
  return (
    <div>
      <QRScanner
        {...qrScannerProps}
        indicator={null}
        // onCode={code => {
        //   setValue(code);
        // }}
        scanType={'goods'}
        onChange={code => {
          console.log(code);
          onChangeProp(code);
        }}
        renderInit={() => {
          return (
            <>
              <InfoCircleTwoTone className={styles.icon} />
              <div>请扫描或输入商品条码</div>
            </>
          );
        }}
        renderLoading={() => {
          return (
            <>
              <Loading className={styles.icon} />
              <div>查询中，请等待</div>
            </>
          );
        }}
        renderError={error => {
          return (
            <>
              <CloseCircleTwoTone twoToneColor={'#eb2f96'} className={styles.icon} />
              <div>{error.msg}</div>
            </>
          );
        }}
      />
    </div>
  );
};

/**
 * 付款码
 */
export type PayQRScannerProps = Omit<QRScannerProps, 'scanType'> & {
  /**付款码付款过程中，中断交易 */
  onRequestCodePayTradeCancel: (param: PosCodePayProceedingInfo) => Promise<void>;
};
export const PayQRScanner: React.FC<PayQRScannerProps> = ({
  onRequestCodePayTradeCancel,
  ...qrScannerProps
}: PayQRScannerProps) => {
  const [loadingInfo, setLoadingInfo] = useState<PosCodePayProceedingInfo>();

  const clear = () => {
    setLoadingInfo(undefined);
  };

  useEffect(() => {
    const id = addWsListener<PosCodePayProceedingInfo>(DOMAIN_PAYMENT, WsActions.PAYING_TIP, msg => {
      setLoadingInfo(msg);
    });
    return () => {
      removeWsListener(id);
      clear();
    };
  }, []);

  return (
    <QRScanner
      {...qrScannerProps}
      scanType={'payAuthCode'}
      indicator={null}
      renderInit={() => {
        return (
          <>
            <img src={require('./qr.png')} style={{ height: 100, marginBottom: 12 }} />
            <div>请扫描客户出示的付款码</div>
          </>
        );
      }}
      renderLoading={() => {
        return (
          <>
            <Loading className={styles.icon} />
            <div>交易中，请等待</div>
            {loadingInfo && (
              <div>
                <div style={{ paddingTop: 3, color: '#999' }}>
                  <LoadingOutlined /> {loadingInfo.msg}
                </div>
                {loadingInfo.tradeStatus === 'WAIT_BUYER_PAY' || loadingInfo.tradeStatus === 'USERPAYING' ? (
                  <div>
                    <PosButton
                      danger
                      onClick={async () => {
                        await onRequestCodePayTradeCancel(loadingInfo);
                        clear();
                      }}>
                      撤销交易
                    </PosButton>
                  </div>
                ) : null}
              </div>
            )}
          </>
        );
      }}
      renderError={error => {
        return (
          <>
            <CloseCircleTwoTone twoToneColor={'#eb2f96'} className={styles.icon} />
            <div>{error.msg}</div>
          </>
        );
      }}
    />
  );
};

// const QRScanner: React.FC<Props> = ({ onChange, mode }: Props) => {
//   const videoRef = useRef(null);
//   const canvasRef = useRef(null);
//
//   const closeCamera = useCallback(() => {
//     if (videoRef && videoRef.current) {
//       try {
//         videoRef.current.srcObject.getTracks().forEach(function(track) {
//           track.stop();
//         });
//       } catch (e) {}
//     }
//   }, [videoRef]);
//
//   const tick = useCallback(() => {
//     const video = videoRef.current;
//     const canvasElement = canvasRef.current;
//     if (!video || !canvasElement) return;
//     const canvas = canvasElement.getContext('2d');
//
//     if (video.readyState === video.HAVE_ENOUGH_DATA) {
//       canvasElement.hidden = false;
//
//       canvasElement.height = video.videoHeight;
//       canvasElement.width = video.videoWidth;
//       canvas.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
//       const imageData = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
//       const code = jsQR(imageData.data, imageData.width, imageData.height, {
//         inversionAttempts: 'dontInvert',
//       });
//       if (code) {
//         drawLine(canvas, code.location.topLeftCorner, code.location.topRightCorner);
//         drawLine(canvas, code.location.topRightCorner, code.location.bottomRightCorner);
//         drawLine(canvas, code.location.bottomRightCorner, code.location.bottomLeftCorner);
//         drawLine(canvas, code.location.bottomLeftCorner, code.location.topLeftCorner);
//
//         if (modeValidator[mode](code.data)) {
//           onChange(code.data);
//           closeCamera();
//         }
//         return;
//       } else {
//         // outputMessage.hidden = false;
//         // outputData.parentElement.hidden = true;
//       }
//     }
//     requestAnimationFrame(tick);
//   }, [videoRef, canvasRef]);
//
//   useEffect(() => {
//     const video = videoRef.current;
//     if (video) {
//       navigator.mediaDevices
//         .getUserMedia({ video: { facingMode: 'environment' } })
//         .then(function(stream) {
//           video.srcObject = stream;
//           video.setAttribute('playsinline', true); // required to tell iOS safari we don't want fullscreen
//           video.play();
//           requestAnimationFrame(tick);
//         });
//     }
//
//     return () => {
//       closeCamera();
//     };
//   }, []);
//
//   return (
//     <div>
//       <canvas ref={canvasRef} hidden style={{ width: '100%' }} />
//       <video ref={videoRef} style={{ display: 'none' }} />
//     </div>
//   );
// };
//
// export default QRScanner;
